import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { memo, useMemo } from 'react';
import useUnitProvider from '/@/core/context/unit';
import { useWeatherForecast } from '/@/hooks/frost-risk';
import { _ } from '/@/locale';
import { getWeatherCodeData } from './weather-codes';

const WeatherForecastMiniCard = memo(({ lat, lng }: { lat: number; lng: number }) => {
    const weatherForecastQuery = useWeatherForecast({ lat, lng });
    const units = useUnitProvider();

    const weatherForecastData = weatherForecastQuery.data?.data;
    const currentForecast = weatherForecastData?.current.data;

    const { todayForecast, minTemp, maxTemp } = useMemo(() => {
        const today = new Date().toISOString().split('T')[0];
        const forecast = weatherForecastData?.forecast.find((f) => f.date === today)?.data?.[0];
        const min = forecast ? Math.min(forecast.day.minTemp, forecast.night.minTemp) : undefined;
        const max = forecast ? Math.max(forecast.day.maxTemp, forecast.night.maxTemp) : undefined;

        return {
            todayForecast: forecast,
            minTemp: min,
            maxTemp: max,
        };
    }, [weatherForecastData?.forecast]);

    const weatherCodeData = useMemo(
        () => getWeatherCodeData(currentForecast?.currentIcon),
        [currentForecast?.currentIcon]
    );

    const currentTempDisplay = useMemo(
        () => (currentForecast?.currentTemp ? units.toTemperature(currentForecast.currentTemp, 2).toString() : null),
        [currentForecast?.currentTemp, units]
    );

    const minTempDisplay = useMemo(
        () => (minTemp ? units.toTemperature(minTemp, 2).toString() : null),
        [minTemp, units]
    );

    const maxTempDisplay = useMemo(
        () => (maxTemp ? units.toTemperature(maxTemp, 2).toString() : null),
        [maxTemp, units]
    );

    if (!weatherForecastData && !weatherForecastQuery.isValidating) return null;

    return (
        <Stack
            direction='row'
            sx={{ background: 'white' }}
            py={1}
            px={3}
            borderRadius={1}
            spacing={3}
            alignItems='center'
            justifyContent='center'
        >
            <Stack sx={{ maxWidth: '35%' }}>
                <Typography variant='subtitle2' color='primary' fontWeight='bold'>
                    {_('Hoy')}
                </Typography>
                <Typography variant='subtitle2' color='primary'>
                    {weatherCodeData?.description ? (
                        _(weatherCodeData?.description)
                    ) : (
                        <Skeleton variant='text' width={100} />
                    )}
                </Typography>
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.5}>
                <Typography variant='h5' color='primary'>
                    {currentTempDisplay ? currentTempDisplay : <Skeleton variant='text' width={80} />}
                </Typography>
                {weatherCodeData?.image ? (
                    <Box
                        component='img'
                        src={weatherCodeData.image}
                        alt={_(weatherCodeData.description)}
                        width={24}
                        height={24}
                        sx={{
                            width: '24px',
                            height: '24px',
                        }}
                    />
                ) : (
                    <Skeleton variant='rounded' width={24} height={24} />
                )}
            </Stack>
            <Stack direction='row' spacing={1}>
                <Typography variant='subtitle2' color='primary'>
                    {_('Min')} {minTempDisplay ? minTempDisplay : <Skeleton variant='text' width={50} />}
                </Typography>
                <Typography variant='subtitle2' color='primary'>
                    {_('Max')} {maxTempDisplay ? maxTempDisplay : <Skeleton variant='text' width={50} />}
                </Typography>
            </Stack>
        </Stack>
    );
});

export default WeatherForecastMiniCard;
